import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

// suit: 0=♠ 1=♥ 2=♦ 3=♣  (red = 1,2)
interface Card { id: number; suit: number; rank: number; faceUp: boolean; }
const SUITS = ['♠', '♥', '♦', '♣'];
const isRed = (s: number) => s === 1 || s === 2;
const rankLabel = (r: number) => (r === 1 ? 'A' : r === 11 ? 'J' : r === 12 ? 'Q' : r === 13 ? 'K' : String(r));

// Levels: draw count (1 = easy, 3 = harder).
const LEVELS = [{ name: 'Draw 1', draw: 1 }, { name: 'Draw 3', draw: 3 }];

type PileKind = 'waste' | 'tableau' | 'foundation';
interface Sel { kind: PileKind; col: number; idx: number; }

function freshDeck(): Card[] {
  const d: Card[] = [];
  let id = 0;
  for (let s = 0; s < 4; s++) for (let r = 1; r <= 13; r++) d.push({ id: id++, suit: s, rank: r, faceUp: false });
  for (let i = d.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  return d;
}

interface GameState {
  stock: Card[];
  waste: Card[];
  foundations: Card[][]; // 4
  tableau: Card[][];     // 7
}

function deal(): GameState {
  const deck = freshDeck();
  const tableau: Card[][] = Array.from({ length: 7 }, () => []);
  let k = 0;
  for (let col = 0; col < 7; col++) {
    for (let n = 0; n <= col; n++) {
      const c = deck[k++];
      c.faceUp = n === col;
      tableau[col].push(c);
    }
  }
  const stock = deck.slice(k).map(c => ({ ...c, faceUp: false }));
  return { stock, waste: [], foundations: [[], [], [], []], tableau };
}

const GameSolitaire: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('solitaire');
  const [level, setLvl] = useState(prog.level || 1);
  const [state, setState] = useState<GameState>(deal);
  const [sel, setSel] = useState<Sel | null>(null);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [won, setWon] = useState(false);
  const [best] = useState(getHighScore('solitaire'));
  const [score, setScore] = useState(0);
  const savedRef = useRef(false);
  const drawCount = LEVELS[Math.min(level - 1, LEVELS.length - 1)].draw;

  const reset = useCallback((lvl: number) => {
    setState(deal());
    setSel(null);
    setMoves(0);
    setSeconds(0);
    setWon(false);
    setScore(0);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('solitaire', lvl);
  }, []);

  useEffect(() => {
    if (won) return;
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [won]);

  const checkWin = (g: GameState) => g.foundations.every(f => f.length === 13);

  useEffect(() => {
    if (won && !savedRef.current) {
      savedRef.current = true;
      const final = Math.max(200, 5000 + level * 1000 - seconds * 5 - moves * 3);
      setScore(final);
      recordScore('solitaire', final, level);
      unlockNext('solitaire', level);
      sfx.win();
    }
  }, [won, seconds, moves, level]);

  // --- Stock / waste ---
  const drawFromStock = () => {
    if (won) return;
    setState(prev => {
      const g = clone(prev);
      if (g.stock.length === 0) {
        // Recycle waste back to stock (face down, reversed).
        g.stock = g.waste.reverse().map(c => ({ ...c, faceUp: false }));
        g.waste = [];
      } else {
        for (let i = 0; i < drawCount && g.stock.length; i++) {
          const c = g.stock.pop()!;
          c.faceUp = true;
          g.waste.push(c);
        }
      }
      return g;
    });
    setSel(null);
    sfx.click();
  };

  // --- Move validation ---
  const canToFoundation = (g: GameState, card: Card) => card.rank === g.foundations[card.suit].length + 1;
  const canToTableau = (g: GameState, card: Card, col: number) => {
    const dest = g.tableau[col];
    if (dest.length === 0) return card.rank === 13;
    const top = dest[dest.length - 1];
    return top.faceUp && isRed(top.suit) !== isRed(card.suit) && top.rank === card.rank + 1;
  };

  const getSelStack = (g: GameState, s: Sel): Card[] => {
    if (s.kind === 'tableau') return g.tableau[s.col].slice(s.idx);
    if (s.kind === 'waste') return g.waste.slice(-1);
    return g.foundations[s.col].slice(-1);
  };

  const removeSelStack = (g: GameState, s: Sel) => {
    if (s.kind === 'tableau') {
      g.tableau[s.col] = g.tableau[s.col].slice(0, s.idx);
      const col = g.tableau[s.col];
      if (col.length && !col[col.length - 1].faceUp) col[col.length - 1].faceUp = true; // flip exposed
    } else if (s.kind === 'waste') {
      g.waste.pop();
    } else {
      g.foundations[s.col].pop();
    }
  };

  const tryMove = (target: { kind: PileKind; col: number }) => {
    if (!sel) return;
    setState(prev => {
      const g = clone(prev);
      const stack = getSelStack(g, sel);
      if (stack.length === 0) return prev;
      const moving = stack[0];

      let ok = false;
      if (target.kind === 'foundation') {
        ok = stack.length === 1 && canToFoundation(g, moving) && moving.suit === target.col;
        if (ok) { removeSelStack(g, sel); g.foundations[target.col].push(moving); }
      } else if (target.kind === 'tableau') {
        ok = canToTableau(g, moving, target.col);
        if (ok) { removeSelStack(g, sel); g.tableau[target.col].push(...stack); }
      }
      if (!ok) return prev;
      setMoves(m => m + 1);
      sfx.place();
      if (checkWin(g)) setWon(true);
      return g;
    });
    setSel(null);
  };

  // Auto-send a card to its foundation if possible (clicking an already-selected card).
  const autoToFoundation = (s: Sel) => {
    setState(prev => {
      const g = clone(prev);
      const stack = getSelStack(g, s);
      if (stack.length !== 1) return prev;
      const card = stack[0];
      if (!canToFoundation(g, card)) return prev;
      removeSelStack(g, s);
      g.foundations[card.suit].push(card);
      setMoves(m => m + 1);
      sfx.score();
      if (checkWin(g)) setWon(true);
      return g;
    });
    setSel(null);
  };

  // --- Click handlers ---
  const clickTableauCard = (col: number, idx: number) => {
    if (won) return;
    const card = state.tableau[col][idx];
    if (!card.faceUp) {
      // clicking a face-down card does nothing unless it's the top (auto-flip handled on move)
      return;
    }
    if (sel) {
      if (sel.kind === 'tableau' && sel.col === col && sel.idx === idx) { autoToFoundation(sel); return; }
      tryMove({ kind: 'tableau', col });
    } else {
      // select this card (and the run below it)
      setSel({ kind: 'tableau', col, idx });
      sfx.move();
    }
  };

  const clickEmptyTableau = (col: number) => {
    if (won || !sel) return;
    tryMove({ kind: 'tableau', col });
  };

  const clickWaste = () => {
    if (won || state.waste.length === 0) return;
    const idx = state.waste.length - 1;
    if (sel && sel.kind === 'waste') { autoToFoundation(sel); return; }
    setSel({ kind: 'waste', col: 0, idx });
    sfx.move();
  };

  const clickFoundation = (suit: number) => {
    if (won) return;
    if (sel) { tryMove({ kind: 'foundation', col: suit }); return; }
    // allow picking up the top foundation card (to play back down)
    if (state.foundations[suit].length) {
      setSel({ kind: 'foundation', col: suit, idx: state.foundations[suit].length - 1 });
      sfx.move();
    }
  };

  const isSelected = (kind: PileKind, col: number, idx: number) =>
    !!sel && sel.kind === kind && sel.col === col && idx >= sel.idx;

  const renderCard = (card: Card, seld: boolean, extra?: React.CSSProperties) => (
    <div
      className={`sol-card ${card.faceUp ? 'up' : 'down'} ${isRed(card.suit) ? 'red' : 'black'} ${seld ? 'sel' : ''}`}
      style={extra}
    >
      {card.faceUp && (
        <>
          <span className="sol-corner tl">{rankLabel(card.rank)}<br />{SUITS[card.suit]}</span>
          <span className="sol-pip">{SUITS[card.suit]}</span>
        </>
      )}
    </div>
  );

  return (
    <div className="gsol">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{moves}</b><i>moves</i></span>
          <span className="gstat"><b>{seconds}s</b><i>time</i></span>
          <span className="gstat"><b>{best}</b><i>best</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ Deal</button>
      </div>

      <div className="g2048-levels">
        {LEVELS.map((lv, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('solitaire').unlockedLevel;
          return (
            <button key={lv.name} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked}>
              {unlocked ? lv.name : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gsol-top">
        {/* Stock */}
        <button className="sol-slot sol-stock" onClick={drawFromStock} aria-label="Draw">
          {state.stock.length > 0 ? <div className="sol-card down" /> : <span className="sol-recycle">↻</span>}
        </button>
        {/* Waste */}
        <button className="sol-slot" onClick={clickWaste} aria-label="Waste">
          {state.waste.length > 0
            ? renderCard(state.waste[state.waste.length - 1], !!sel && sel.kind === 'waste')
            : <span className="sol-empty" />}
        </button>

        <div className="sol-spacer" />

        {/* Foundations */}
        {[0, 1, 2, 3].map(suit => (
          <button key={suit} className={`sol-slot sol-found ${isRed(suit) ? 'red' : 'black'}`} onClick={() => clickFoundation(suit)} aria-label={`Foundation ${SUITS[suit]}`}>
            {state.foundations[suit].length > 0
              ? renderCard(state.foundations[suit][state.foundations[suit].length - 1], !!sel && sel.kind === 'foundation' && sel.col === suit)
              : <span className="sol-found-mark">{SUITS[suit]}</span>}
          </button>
        ))}
      </div>

      <div className="gsol-tableau">
        {state.tableau.map((col, ci) => (
          <div key={ci} className="sol-col" onClick={() => col.length === 0 && clickEmptyTableau(ci)}>
            {col.length === 0 && <div className="sol-slot sol-empty-col" />}
            {col.map((card, ri) => (
              <div
                key={card.id}
                className="sol-card-wrap"
                style={{ top: ri * 22 }}
                onClick={(e) => { e.stopPropagation(); clickTableauCard(ci, ri); }}
              >
                {renderCard(card, isSelected('tableau', ci, ri))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {won && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">🏆</div>
            <h3>You won!</h3>
            <p>{moves} moves · {seconds}s · <b>{score}</b> points{level < LEVELS.length ? ' · next level unlocked' : ''}</p>
            <div className="go-actions">
              {level < LEVELS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>New deal</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Click a card to pick it up, click where it goes. Click a selected card again to send it to a foundation. Click the stock to draw.</p>
    </div>
  );
};

function clone(g: GameState): GameState {
  return {
    stock: g.stock.map(c => ({ ...c })),
    waste: g.waste.map(c => ({ ...c })),
    foundations: g.foundations.map(f => f.map(c => ({ ...c }))),
    tableau: g.tableau.map(col => col.map(c => ({ ...c }))),
  };
}

export default GameSolitaire;
